import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import {
  CHROME_BUTTON_CLASS,
  CHROME_CHECKBOX_CLASS,
  CHROME_POPOVER_CLASS,
  CHROME_SHEET_DONE_CLASS,
  CHROME_SHEET_TITLE_CLASS,
  LAYERS_PANEL_ID,
  chromeButtonLabelClass,
  chromeIconButtonClass,
} from '../../lib/chrome_config'
import {
  COVERAGE_DRAWN,
  COVERAGE_NAMED,
  COVERAGE_NONE,
  SIZE_CLASSES,
  SIZE_CLASS_LABELS,
  WINERY_DERIVED_LEGEND_LABEL,
  WINERY_DERIVED_POINT_OPACITY,
  WINERY_SIZE_COLORS,
  WINERY_UNKNOWN_COLOR,
} from '../../lib/map_config'
import {
  ClusterChip,
  CountrySwatch,
  RibbonSwatch,
  SelectedCountrySwatch,
  SelectedSwatch,
  TastingCount,
  TastingPin,
  TastingWinery,
  VineyardSwatch,
  WineryDot,
  WishlistPin,
} from './plate_key'

interface LegendEntry {
  id: string
  /**
   * The mark, as the map draws it (`plate_key.tsx`). Every swatch is one
   * `aria-hidden` span whose border is the mark's EDGE and whose children are
   * what the edge encloses, so a tint or a ribbon fades at its own opacity
   * inside an edge at full strength.
   */
  swatch: ReactNode
  label: string
  /** Keys something only a signed-in reader has, so a visitor's key omits it. */
  journal?: boolean
}

/**
 * ONE ENTRY PER MEANING. The lists are literals so that a duplicate is visible
 * at a glance, and `layer_toggles.test.tsx` asserts the labels are unique
 * across every group — which is the check that would have caught the defect
 * P4 shipped: it added the two depth-keyed appellation rows and left P1's flat
 * `Appellation` row below them, so the key said "Appellation" twice with two
 * different swatches and React rendered two `<li>` under one `key`.
 *
 * `id` rather than the label as the React key, for the same reason.
 *
 * Grouped by what is being keyed — what the atlas holds for a country, how
 * deep an appellation is nested, what a dot's colour says, what the ground is
 * — because twenty rows in one run made the reader find the three that answer
 * their question. The groups are headings, not fieldsets: nothing in the key
 * is a control.
 *
 * This is the WHOLE key. The plate ribbon along the bottom of the map
 * (`plate_ribbon.tsx`) shows the rows of it that apply to what is on screen,
 * with the same swatches, so the two cannot describe one mark two ways.
 */
const LEGEND_GROUPS: { id: string; title: string; visitorTitle?: string; entries: LegendEntry[] }[] = [
  {
    id: 'countries',
    title: 'Countries',
    entries: [
      // Three KINDS of mark, not three strengths of one: a tint with a solid
      // ribbon inside the coast, a fainter tint with a dashed line, and bare
      // ground. They were three hues at three opacities, and the faintest was a
      // grey wash over most of the planet; a country with nothing imported is
      // now simply the land.
      {
        id: 'drawn',
        swatch: <CountrySwatch coverage={COVERAGE_DRAWN} />,
        label: 'Appellations drawn',
      },
      {
        id: 'named',
        swatch: <CountrySwatch coverage={COVERAGE_NAMED} />,
        label: 'Appellations imported, no boundaries published',
      },
      {
        id: 'none',
        swatch: <CountrySwatch coverage={COVERAGE_NONE} />,
        label: 'No appellations yet',
      },
    ],
  },
  {
    id: 'appellations',
    title: 'Appellations',
    entries: [
      { id: 'appellation', swatch: <RibbonSwatch depth={0} />, label: 'Appellation' },
      // DEPTH IS THE RIBBON, NOT THE GROUND. An appellation is a hairline with
      // a ribbon inside it and no fill, so the ground inside a sub-appellation
      // is the same chalk as the ground outside it; what says "nested" is that
      // its ribbon is narrower and a deeper claret than its parent's, and sits
      // inside it. The wording used to be "darker inside its parent", which was
      // true of stacked fills (P4's KTD13) and is false of a map with none.
      {
        id: 'nested',
        swatch: <RibbonSwatch depth={1} />,
        label: 'Nested appellation — a narrower, stronger ribbon inside its parent’s',
      },
      // The data has three tiers, and so does the ribbon. The few appellations
      // nested deeper than that draw as this one.
      {
        id: 'nested-deeper',
        swatch: <RibbonSwatch depth={2} />,
        label: 'Nested deeper — narrower and stronger again',
      },
      // The only warm, saturated thing on the plate, and the only state that
      // dims everything else: a row of its own, because amber read as "another
      // kind of appellation" is the misreading a key is for.
      { id: 'selected', swatch: <SelectedSwatch />, label: 'Selected — the place the open panel describes' },
      // A country's selection is a mark of its own: a soft band inside its
      // coast, because its outline is too coarse to carry a crisp one.
      { id: 'selected-country', swatch: <SelectedCountrySwatch />, label: 'Selected country' },
    ],
  },
  {
    id: 'wineries',
    title: 'Wineries and pins',
    visitorTitle: 'Wineries',
    entries: [
      // P6a's winery size classes, and its unknown. The unknown gets a line of
      // its own for the same reason the nothing-yet country does: it is nearly
      // every dot on the map, and a neutral dot with no key reads as a
      // rendering fault rather than as a size nobody has measured yet.
      ...SIZE_CLASSES.map((sizeClass) => ({
        id: `size-${sizeClass}`,
        swatch: <WineryDot color={WINERY_SIZE_COLORS[sizeClass]} />,
        label: SIZE_CLASS_LABELS[sizeClass],
      })),
      // "Not stated", the words the plate ribbon and a producer's own Size row
      // use. It read "unknown size" here, a second name for one state.
      {
        id: 'size-unknown',
        swatch: <WineryDot color={WINERY_UNKNOWN_COLOR} />,
        label: 'Winery — size not stated',
      },
      // KTD6's opacity axis. The map dot is the one surface where a visual-only
      // difference is unavoidable — a 5px circle has no room for the word the
      // panel's badge carries — which is exactly why it needs a line here. After
      // P6c almost every coloured dot is an inference from a catalogue count or
      // a platform, so without this a reader has no way to learn that the faded
      // ones are the guesses, and a faded dot reads as a rendering fault. Drawn
      // in a class colour rather than the neutral, because what the fade
      // qualifies is the CLASS.
      {
        id: 'size-derived',
        swatch: <WineryDot color={WINERY_SIZE_COLORS.mid} opacity={WINERY_DERIVED_POINT_OPACITY} />,
        label: WINERY_DERIVED_LEGEND_LABEL,
      },
      // A count, not a class: a paper chip with a teal ring, which is why it
      // needs saying that the number is wineries.
      { id: 'cluster', swatch: <ClusterChip />, label: 'Wineries nearby, counted' },
      // The reader's own marks, every form of them. The key had one row, a
      // plain dark dot two rows under the winery's plain dark dot, while the
      // map's heaviest marks — the numbered discs — were in no key at all.
      // First of them, the one that stands ON a winery: it follows the rows
      // that key a winery's own dot.
      { id: 'tasting-winery', swatch: <TastingWinery />, label: 'A winery you have wines from', journal: true },
      { id: 'tasting', swatch: <TastingPin />, label: 'A tasting of yours', journal: true },
      { id: 'tasting-count', swatch: <TastingCount />, label: 'Your tastings in one place, counted', journal: true },
      {
        id: 'tasting-country',
        swatch: <TastingCount country />,
        label: 'Your tastings known only by country',
        journal: true,
      },
      { id: 'wishlist', swatch: <WishlistPin />, label: 'On your wishlist', journal: true },
    ],
  },
  {
    id: 'ground',
    title: 'Ground',
    // The shading is slope and carries no data, so it has no row; the vines
    // are the one thing on the ground that a wine atlas has to name.
    entries: [{ id: 'vineyard', swatch: <VineyardSwatch />, label: 'Vineyard' }],
  },
]

interface LayerTogglesProps {
  /** Whether the popover (a bottom sheet on a phone) is showing. */
  open: boolean
  onToggleOpen: (open: boolean) => void
  /** A detail panel is open beside the bar, which leaves the bar less room for words. */
  beside?: boolean
  /**
   * False for a visitor. The atlas is public and a journal is not, so a reader
   * with no account has no tastings or wishlist to draw — and two rows that
   * could only ever read "Nothing logged yet" would describe a feature they
   * have not been offered rather than a layer that is empty.
   */
  journalLayers?: boolean
  countriesVisible: boolean
  regionsVisible: boolean
  regionsEnabled: boolean
  wineriesVisible: boolean
  wineriesEnabled: boolean
  tastingsVisible: boolean
  wishlistVisible: boolean
  tastingCount: number
  wishlistCount: number
  /** Tastings the atlas could place nowhere, reported rather than silently dropped. */
  unpinnedTastings: number
  onToggleCountries: (visible: boolean) => void
  onToggleRegions: (visible: boolean) => void
  onToggleWineries: (visible: boolean) => void
  onToggleTastings: (visible: boolean) => void
  onToggleWishlist: (visible: boolean) => void
}

// The whole row is the target, 44px tall under a thumb and 40px under a
// pointer, with the real checkbox at its right edge. `has-[:disabled]` greys
// the row from the input's own state, so a disabled layer cannot be styled as
// enabled by a class somebody forgot to toggle.
const ROW_CLASS =
  '-mx-2 flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm hover:bg-black/[0.03] ' +
  'has-[:disabled]:text-ink-soft has-[:disabled]:hover:bg-transparent md:min-h-10'

// Indented to the row's name: the symbol's 20px box plus the row's gap.
const REASON_CLASS = '-mt-1.5 pb-1 pl-8 text-xs text-ink-soft'

// Each row wears the mark its layer draws on the map, so the control and the
// thing it controls can be matched by eye. One 20px box for all five — the
// width of a key patch — so a round mark and a square one leave the names on a
// single left edge, and a toggle's mark is the same drawing as its row in the
// key below.
const Mark = ({ children }: { children: ReactNode }) => (
  <span aria-hidden="true" className="grid h-3.5 w-5 shrink-0 place-items-center">
    {children}
  </span>
)

/**
 * R9's layer controls, and the key to the marks — which countries have
 * appellations at all, what a narrower ribbon inside a wider one means, what a
 * dot's colour says.
 *
 * An explicit toggle wins over the zoom rule: crossing the region threshold
 * still fetches the layer and adds it, but leaves a reader-hidden layer hidden
 * until they turn it back on, because a control that silently reverts is worse
 * than no control. Below the threshold the region toggle is present and
 * disabled with its reason, rather than enabled-but-inert, which reads as broken.
 *
 * This is also the seam P2's tastings and wishlist layers plug into, which is
 * why it exists now rather than being retrofitted around them.
 *
 * **A button and a popover, not a permanent box.** The box was 315x469 on a
 * 390x844 phone — 45% of the screen, on every view, to hold five toggles of
 * which three are usually disabled and a twelve-row key of which at most five
 * rows apply to what is drawn. The controls and the key are one click away
 * instead, and the map gets the screen. The component returns a FRAGMENT, the
 * button and then the surface, so both are direct children of the header's
 * cluster: the button takes its place in the row and the surface positions
 * against the cluster (a popover from `md`, a bottom sheet below it).
 *
 * **The surface stays MOUNTED while closed and is hidden with `hidden`**, which
 * is the opposite of what the filters do and is deliberate. `display: none`
 * takes it out of the focus order and the accessibility tree, which is all
 * closing has to mean — and a disabled toggle's reason stays in the DOM for its
 * checkbox's `aria-describedby` to resolve against whether or not anyone is
 * looking at it.
 */
export default function LayerToggles({
  open,
  onToggleOpen,
  beside = false,
  journalLayers = true,
  countriesVisible,
  regionsVisible,
  regionsEnabled,
  wineriesVisible,
  wineriesEnabled,
  tastingsVisible,
  wishlistVisible,
  tastingCount,
  wishlistCount,
  unpinnedTastings,
  onToggleCountries,
  onToggleRegions,
  onToggleWineries,
  onToggleTastings,
  onToggleWishlist,
}: LayerTogglesProps) {
  const button = useRef<HTMLButtonElement>(null)

  // Escape closes the surface when focus is inside it or on its button, and
  // only then. The detail panel listens for Escape on `document`, so the key is
  // stopped here: a reader dismissing the layer list has not asked to be
  // navigated off the place they are reading. Focus goes back to the button,
  // because the surface it was in is about to leave the focus order.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !open) return

    event.stopPropagation()
    onToggleOpen(false)
    button.current?.focus()
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={LAYERS_PANEL_ID}
        title="Layers"
        onClick={() => onToggleOpen(!open)}
        onKeyDown={onKeyDown}
        className={`${CHROME_BUTTON_CLASS} ${chromeIconButtonClass(beside)}`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        >
          <path d="m10 3 7 3.5-7 3.5-7-3.5L10 3Z" />
          <path d="m3 10 7 3.5 7-3.5" />
          <path d="m3 13.5 7 3.5 7-3.5" />
        </svg>
        {/* The word is the button's name at every width and its visible label
            wherever the bar has room for it (`chromeButtonLabelClass`). */}
        <span className={chromeButtonLabelClass(beside)}>Layers</span>
      </button>

      <div
        id={LAYERS_PANEL_ID}
        data-testid={LAYERS_PANEL_ID}
        onKeyDown={onKeyDown}
        className={`${open ? '' : 'hidden '}${CHROME_POPOVER_CLASS} overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 md:pb-4`}
      >
        <fieldset>
          <legend className={CHROME_SHEET_TITLE_CLASS}>Layers</legend>

          <label className={`${ROW_CLASS} mt-1`}>
            <Mark>
              <CountrySwatch coverage={COVERAGE_DRAWN} />
            </Mark>
            <span className="flex-1">Countries</span>
            <input
              type="checkbox"
              className={CHROME_CHECKBOX_CLASS}
              checked={countriesVisible}
              onChange={(event) => onToggleCountries(event.target.checked)}
            />
          </label>

          <label className={ROW_CLASS}>
            {/* A hairline with a ribbon inside it and clean ground in the
                middle, where the country's patch is tinted all over: an
                appellation is drawn as a boundary, a country as a wash. */}
            <Mark>
              <RibbonSwatch depth={0} />
            </Mark>
            <span className="flex-1">Appellations</span>
            <input
              type="checkbox"
              className={CHROME_CHECKBOX_CLASS}
              checked={regionsVisible}
              disabled={!regionsEnabled}
              aria-describedby={regionsEnabled ? undefined : 'regions-disabled-reason'}
              onChange={(event) => onToggleRegions(event.target.checked)}
            />
          </label>

          {!regionsEnabled && (
            <p id="regions-disabled-reason" data-testid="regions-disabled-reason" className={REASON_CLASS}>
              Zoom in to show appellations
            </p>
          )}

          <label className={ROW_CLASS}>
            {/* The neutral dot, which is what nearly every winery on the map
                is. It wore the mid-size teal, a colour no dot on the map may
                have at all. */}
            <Mark>
              <WineryDot color={WINERY_UNKNOWN_COLOR} />
            </Mark>
            <span className="flex-1">Wineries</span>
            <input
              type="checkbox"
              className={CHROME_CHECKBOX_CLASS}
              checked={wineriesVisible}
              disabled={!wineriesEnabled}
              aria-describedby={wineriesEnabled ? undefined : 'wineries-disabled-reason'}
              onChange={(event) => onToggleWineries(event.target.checked)}
            />
          </label>

          {/* Disabled with its reason rather than absent, so an atlas with the
              places imported and the producers not yet reads as a step missing
              rather than as a feature that was never built. */}
          {!wineriesEnabled && (
            <p id="wineries-disabled-reason" data-testid="wineries-disabled-reason" className={REASON_CLASS}>
              No wineries imported yet
            </p>
          )}

          {/* R21's two journal layers. Each is disabled with its reason when it
              has nothing to show, matching how the region toggle behaves below
              its zoom threshold -- a control that is enabled but inert reads as
              broken. ABSENT for a visitor, which is the other rule: see
              `journalLayers` above. */}
          {journalLayers && (
            <>
              <label className={ROW_CLASS}>
                {/* The journal's own marks: the ringed claret disc for a
                    tasting, the ring alone for a wish. */}
                <Mark>
                  <TastingPin />
                </Mark>
                <span className="flex-1">My tastings</span>
                <input
                  type="checkbox"
                  className={CHROME_CHECKBOX_CLASS}
                  checked={tastingsVisible}
                  disabled={tastingCount === 0}
                  aria-describedby={tastingCount === 0 ? 'tastings-disabled-reason' : undefined}
                  onChange={(event) => onToggleTastings(event.target.checked)}
                />
              </label>

              {tastingCount === 0 && (
                <p id="tastings-disabled-reason" data-testid="tastings-disabled-reason" className={REASON_CLASS}>
                  Nothing logged yet
                </p>
              )}

              {/* A tasting the atlas cannot place is a real state. Saying so is
                  the difference between an honest limit and a pin the reader
                  assumes is there. Under the row it is about: it used to come
                  after the wishlist's, and read as a fact about the wishlist. */}
              {unpinnedTastings > 0 && (
                <p data-testid="unpinned-tastings" className={REASON_CLASS}>
                  {unpinnedTastings} more {unpinnedTastings === 1 ? 'has' : 'have'} no place on the map to pin to
                </p>
              )}

              <label className={ROW_CLASS}>
                <Mark>
                  <WishlistPin />
                </Mark>
                <span className="flex-1">My wishlist</span>
                <input
                  type="checkbox"
                  className={CHROME_CHECKBOX_CLASS}
                  checked={wishlistVisible}
                  disabled={wishlistCount === 0}
                  aria-describedby={wishlistCount === 0 ? 'wishlist-disabled-reason' : undefined}
                  onChange={(event) => onToggleWishlist(event.target.checked)}
                />
              </label>

              {wishlistCount === 0 && (
                <p id="wishlist-disabled-reason" data-testid="wishlist-disabled-reason" className={REASON_CLASS}>
                  Nothing on the wishlist
                </p>
              )}
            </>
          )}
        </fieldset>

        {/* The whole key, one row per meaning. The plate ribbon on the map
            shows the rows that apply to what is on screen; this is where every
            row is, including the ones for marks the camera is not near.

            The three country rows key WHAT THE ATLAS HOLDS for a country, and
            the three appellation rows key a DEPTH. European appellations nest,
            Margaux inside Haut-Médoc inside Médoc inside Bordeaux, and each
            level is drawn as a narrower ribbon in a deeper claret, inside its
            parent's. It was stacked translucent fills, so that depth read as
            darkness (P4's KTD13) — which also tinted the whole page at
            appellation zoom, and is why it was replaced. The rows carry the
            map's own tints, layer opacity and widths rather than three flat
            swatches: they differ only in depth, and a key that flattened that
            would be the puzzle over again.

            In each row the swatch is the first `aria-hidden` span
            (`plate_key.tsx`). A dot carries its colour and opacity INLINE on
            that span; a patch carries its edge there as a border and what the
            edge encloses on the spans inside it, so an opacity fades the
            inside and not the edge. All of it reads the constants in
            `chrome_config.ts`, each named for the map constant it stands for,
            and the tests hold the swatches to those — so a key that documents
            a different mark from the one drawn fails there.

            Every swatch sits in the same 20px box the toggles' marks use, so a
            dot and a patch leave the labels on one left edge. */}
        <div data-testid="coverage-legend" className="mt-3 border-t border-black/5 pt-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">Key</h3>

          <div className="mt-2 space-y-3 text-xs text-ink-soft">
            {LEGEND_GROUPS.map((group) => (
              <div key={group.id}>
                <p className="mb-1 font-medium text-ink">
                  {journalLayers ? group.title : (group.visitorTitle ?? group.title)}
                </p>
                <ul className="space-y-1.5">
                  {group.entries
                    .filter((entry) => journalLayers || !entry.journal)
                    .map((entry) => (
                      <li key={entry.id} className="flex items-start gap-2 leading-[1.3]">
                        <span className="grid h-4 w-5 shrink-0 place-items-center">{entry.swatch}</span>
                        {entry.label}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* On a phone, the sheet's way out, beside its title: this sheet had a
            title and no Done, so the only way to put it away was to find the
            Layers button again at the top of the screen. From `md` it is a
            popover hanging from its own button, and the button is the way out.

            LAST in the document and pinned to the top corner, so it is beside
            the title to the eye and after the last checkbox to the keyboard:
            the first tab stop in the sheet stays the first layer. */}
        <button
          type="button"
          data-testid="layers-done"
          onClick={() => {
            onToggleOpen(false)
            button.current?.focus()
          }}
          className={`absolute right-4 top-3 md:hidden ${CHROME_SHEET_DONE_CLASS}`}
        >
          Done
        </button>
      </div>
    </>
  )
}
